import React, { useMemo } from 'react';
import { useSelect } from 'downshift';
import cx from 'classnames';
import { Icon } from 'js/components';
import { faSquareCheck } from '@fortawesome/pro-solid-svg-icons';
import { faSquare } from '@fortawesome/pro-regular-svg-icons';
import { getCategoryPaletteColor } from 'js/utils/color';
import {
  flattenCapabilityTree,
  sameIdSet,
} from 'js/utils/capability-treemap-scores';
import styles from './CapabilityFilterCombobox.module.scss';

function itemToLabel(item) {
  return item ? item.name || String(item.id) : '';
}

export function getCapabilityFilterLabel(
  capabilities = [],
  selectedIds = [],
  defaultIds = []
) {
  const flatItems = flattenCapabilityTree(capabilities);
  if (flatItems.length === 0) {
    return '';
  }
  if (selectedIds.length === 0) {
    return 'None selected';
  }
  if (selectedIds.length === flatItems.length) {
    return 'All capabilities';
  }
  if (sameIdSet(selectedIds, defaultIds)) {
    return 'Scored capabilities';
  }
  if (selectedIds.length === 1) {
    const item = flatItems.find((c) => c.id === selectedIds[0]);
    return itemToLabel(item) || '1 capability';
  }
  return `${selectedIds.length} of ${flatItems.length} capabilities`;
}

function CapabilityTreeNodes({
  nodes,
  depth = 0,
  itemIndexById,
  selectedIdSet,
  highlightedIndex,
  getItemProps,
}) {
  return (nodes || []).map((capability) => {
    const index = itemIndexById.get(capability.id);
    if (index == null) {
      return null;
    }
    const children = capability.children || [];
    const hasChildren = children.length > 0;
    const isSelected = selectedIdSet.has(capability.id);
    const { background } = getCategoryPaletteColor(depth);
    const item = {
      id: capability.id,
      name: capability.name,
      depth,
      capability,
    };

    return (
      <li key={capability.id} className={styles.node}>
        {hasChildren && <div className={styles['child-span']} />}
        <div
          className={cx(styles['node-row'], {
            [styles.highlighted]: highlightedIndex === index,
          })}
          style={{ '--tier-color': background }}
          {...getItemProps({ item, index })}
        >
          <span className={styles['node-state']}>
            <Icon icon={isSelected ? faSquareCheck : faSquare} fixedWidth />
          </span>
          <span className={styles['node-name']}>{capability.name}</span>
        </div>
        {hasChildren && (
          <ul className={styles['node-children']}>
            <CapabilityTreeNodes
              nodes={children}
              depth={depth + 1}
              itemIndexById={itemIndexById}
              selectedIdSet={selectedIdSet}
              highlightedIndex={highlightedIndex}
              getItemProps={getItemProps}
            />
          </ul>
        )}
      </li>
    );
  });
}

export default function CapabilityFilterCombobox({
  placeholder = 'Select capabilities…',
  capabilities = [],
  selectedIds = [],
  defaultIds = [],
  onChange,
  disabled = false,
  id: inputId,
}) {
  const flatItems = useMemo(
    () => flattenCapabilityTree(capabilities),
    [capabilities]
  );

  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const displayLabel = useMemo(
    () => getCapabilityFilterLabel(capabilities, selectedIds, defaultIds),
    [capabilities, selectedIds, defaultIds]
  );

  const itemIndexById = useMemo(() => {
    const map = new Map();
    flatItems.forEach((item, index) => {
      map.set(item.id, index);
    });
    return map;
  }, [flatItems]);

  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
  } = useSelect({
    items: flatItems,
    selectedItem: null,
    itemToString: itemToLabel,
    stateReducer: (state, actionAndChanges) => {
      const { type, changes } = actionAndChanges;
      switch (type) {
        case useSelect.stateChangeTypes.ItemClick:
          return {
            ...changes,
            isOpen: true,
            highlightedIndex: state.highlightedIndex,
          };
        default:
          return changes;
      }
    },
    onSelectedItemChange: ({ selectedItem }) => {
      if (!selectedItem) {
        return;
      }
      const next = selectedIdSet.has(selectedItem.id)
        ? selectedIds.filter((id) => id !== selectedItem.id)
        : [...selectedIds, selectedItem.id];
      onChange(next);
    },
  });

  const {
    type: _toggleType,
    onClick: onToggleClick,
    ...toggleProps
  } = getToggleButtonProps({
    disabled,
    id: inputId,
  });

  const selectAll = () => {
    onChange(flatItems.map((item) => item.id));
  };

  const clearAll = () => {
    onChange([]);
  };

  const selectDefault = () => {
    onChange([...(defaultIds || [])]);
  };

  return (
    <div className={cx('combobox', styles.root)}>
      <div
        className={cx(styles['toggle-button'], 'input-group limited-width-lg')}
        onClick={disabled ? undefined : onToggleClick}
      >
        <input
          readOnly
          placeholder={placeholder}
          className="form-control"
          value={displayLabel}
          {...toggleProps}
        />
        <div className="input-group-append">
          <span aria-hidden="true">
            {isOpen ? (
              <Icon icon={['far', 'chevron-up']} fixedWidth />
            ) : (
              <Icon icon={['far', 'chevron-down']} fixedWidth />
            )}
          </span>
        </div>
      </div>
      <ul
        {...getMenuProps()}
        className="combobox-items"
        style={!isOpen ? { display: 'none' } : {}}
      >
        {isOpen && (
          <>
            <li
              className="combobox-plain-item"
              onMouseDown={(e) => e.preventDefault()}
            >
              <button
                type="button"
                className="btn btn-link btn-xs p-0 mr-3"
                onClick={selectAll}
              >
                Select all
              </button>
              <button
                type="button"
                className="btn btn-link btn-xs p-0 mr-3"
                onClick={clearAll}
              >
                Clear
              </button>
              <button
                type="button"
                className="btn btn-link btn-xs p-0"
                onClick={selectDefault}
              >
                Scored
              </button>
            </li>
            {flatItems.length === 0 ? (
              <li className="combobox-item text-muted">No capabilities</li>
            ) : (
              <CapabilityTreeNodes
                nodes={capabilities}
                itemIndexById={itemIndexById}
                selectedIdSet={selectedIdSet}
                highlightedIndex={highlightedIndex}
                getItemProps={getItemProps}
              />
            )}
          </>
        )}
      </ul>
    </div>
  );
}
